/**
 * SEO HEADING STRUCTURE - Study Stream
 * =====================================
 * 
 * TITLE: Study Stream | CSW | Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Join CSW's free 24/7 Study Stream - unlimited virtual study rooms with no time limits...
 * 
 * H1 (1 tag):
 *   - "Study Stream" (hero section)
 * 
 * H2 (13 tags with keywords):
 *   - "Study Stream Community: Study Together Online"
 *   - "Gamified Study Stream: Interactive Study Experience"
 *   - "Study Stream Focus Room: Music & Media Integration"
 *   - "Study Stream Sessions: Study Together, Stay Motivated"
 *   - "Study Stream Support: Flexible Study Solutions"
 *   - "Study Stream Wellness: Mindfulness Integration"
 *   - "24/7 Study Stream Access: Free and Unlimited"
 *   - "24/7 Study Room: Virtual Focus Room Online"
 *   - "Study With Strangers Online: Global Community"
 *   - "How Study Stream Works"
 *   - "Study Stream FAQ"
 *   - "Join 24/7 StudyStream Free Today!"
 * 
 * H3 (16+ tags):
 *   - Feature titles across all sections
 */

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { StudyStream as StudyStreamSection } from "@/components/StudyStream";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  Users, Clock, Globe, Zap, Video, MessageCircle, Play, ArrowRight, Infinity,
  Target, Heart, ThumbsUp, Timer, Unlock, Shield, Sparkles, Monitor,
  Trophy, Award, Music, Youtube, BookOpen, Brain, Smile, Star, CheckCircle,
  Headphones, GraduationCap, ListChecks, HandHeart, HelpCircle
} from "lucide-react";

const socialFeatures = [
  {
    icon: MessageCircle,
    title: "Live Group Chat",
    description: "Connect via text with others in your study room. Share what you're working on, ask quick questions, or send a thumbs-up to encourage peers.",
  },
  {
    icon: Target,
    title: "Goal Sharing",
    description: "Share your goal or to-do at the beginning of each session. Seeing everyone's goals adds gentle pressure to follow through.",
  },
  {
    icon: ThumbsUp,
    title: "Progress Updates & Celebrations",
    description: "Hit a milestone or finished a pomodoro cycle? Update the group or use reaction emojis to celebrate. Everyone's wins inspire others.",
  },
  {
    icon: Heart,
    title: "No Toxic Competition",
    description: "Study Stream is about mutual support. No leaderboards or competitive rankings – focus on personal progress and collective encouragement.",
  },
];

const gamificationFeatures = [
  {
    icon: Trophy,
    title: "Achievement Tracking & Rewards",
    description: "Monitor progress with visible milestones, encouraging continuous improvement. Earn rewards as you reach study goals.",
  },
  {
    icon: Target,
    title: "Goal Setting",
    description: "Set clear, actionable study goals – daily or weekly – and see your study stats and achievements displayed.",
  },
  {
    icon: Award,
    title: "Leaderboards",
    description: "See your standing within the community, offering a fun incentive to keep pushing towards your academic aspirations.",
  },
  {
    icon: Star,
    title: "Milestone Celebrations",
    description: "Acknowledge and celebrate your achievements, no matter how small. Every win counts on your study journey.",
  },
];

const mediaFeatures = [
  {
    icon: Youtube,
    title: "Synchronized YouTube Watching",
    description: "Watch educational videos or relaxing playlists together with study buddies – perfect for group revision sessions or winding down.",
  },
  {
    icon: Music,
    title: "Integrated Study Music",
    description: "Choose from soothing tracks to create a calming background for focused study sessions. Match your study mood with the right vibe.",
  },
  {
    icon: Headphones,
    title: "Ambiance Options",
    description: "Range of ambiance settings to keep you in the zone – from lo-fi beats to nature sounds to complete silence.",
  },
  {
    icon: Play,
    title: "Interactive Learning",
    description: "Perfect for group revision sessions, interactive learning, or simply relaxing together during breaks.",
  },
];

const supportFeatures = [
  {
    icon: GraduationCap,
    title: "Guidance from Tutors",
    description: "Get assistance with difficult topics in interactive boot camps designed to deepen your understanding.",
  },
  {
    icon: ListChecks,
    title: "Task Management Tools",
    description: "Organize tasks and stay on top of deadlines with easy-to-use planning tools integrated into your study space.",
  },
  {
    icon: BookOpen,
    title: "Study Flow Options",
    description: "Customizable study flows that can be tailored for each user – from high school to college graduates.",
  },
  {
    icon: Timer,
    title: "Customizable Timers",
    description: "Choose intervals that best suit your focus style, or use preset timers like Pomodoro to create structure.",
  },
];

const wellnessFeatures = [
  {
    icon: Brain,
    title: "Guided Mindfulness Sessions",
    description: "Reconnect with yourself and reduce stress through structured mindfulness breaks during study sessions.",
  },
  {
    icon: Smile,
    title: "Wellness Resources",
    description: "Find techniques for balancing study with relaxation to prevent burnout and maintain peak performance.",
  },
  {
    icon: HandHeart,
    title: "Mental Refreshers",
    description: "Short mental breaks help you recharge, improving both academic performance and overall well-being.",
  },
  {
    icon: Heart,
    title: "Burnout Prevention",
    description: "Balance productivity with well-being through guided techniques to manage stress and maintain energy.",
  },
];

const roomFeatures = [
  {
    icon: Clock,
    title: "24/7 Availability",
    description: "The Study Stream never sleeps. There are always rooms open with learners from different time zones. Need to study at 3 AM? You'll find others online.",
  },
  {
    icon: Zap,
    title: "Drop-in, Drop-out Flexibility",
    description: "Join a room with a single click – no scheduling required. Stay for as long or as short as you want. New members can come and go freely.",
  },
  {
    icon: Monitor,
    title: "Minimalist, Distraction-Free Interface",
    description: "Clean and simple interface with your task list, timer, and video grid. No flashy animations or noisy notifications – just a tidy online desk.",
  },
  {
    icon: Shield,
    title: "Personal and Shared Accountability",
    description: "Control your own tools and notes while knowing others can see you're present. This gentle peer pressure keeps you on task.",
  },
];

const accessFeatures = [
  {
    icon: Infinity,
    title: "Unlimited Session Time",
    description: "No time limits on how long you can study. Stay in a room for hours if needed.",
  },
  {
    icon: Unlock,
    title: "No Usage Caps",
    description: "Join as many sessions as you want, whenever you want. No daily or monthly limits.",
  },
  {
    icon: Globe,
    title: "Global Accessibility",
    description: "Available in multiple languages for students and professionals worldwide.",
  },
  {
    icon: Sparkles,
    title: "Core Features Free Forever",
    description: "Unlike competitors who restrict free users, all essential features are completely free.",
  },
];

const globalCommunityFeatures = [
  {
    icon: Globe,
    title: "Global Student Network",
    description: "Collaborate with peers, engage in study groups, and meet new friends from around the world.",
  },
  {
    icon: Users,
    title: "Community Events",
    description: "Join events that help build friendships and expand your network, adding a social dimension to your study experience.",
  },
  {
    icon: CheckCircle,
    title: "Shared Accountability",
    description: "Improve productivity and focus by studying alongside a supportive community with positive peer pressure.",
  },
  {
    icon: Heart,
    title: "Career & Life Advice",
    description: "Get career advice and life tips from other students worldwide in a supportive environment.",
  },
];

const howItWorks = [
  {
    step: "1",
    title: "Choose a Room",
    description: "Browse available study rooms or create your own. Pick a vibe that matches your mood.",
  },
  {
    step: "2",
    title: "Join & Connect",
    description: "Enter the room with one click. Enable your webcam to join the community (optional).",
  },
  {
    step: "3",
    title: "Study Together",
    description: "Focus on your work while surrounded by others doing the same. Stay motivated and accountable.",
  },
];

const faqData = [
  {
    question: "What is a virtual study room?",
    answer: "A virtual study room is an online space where students and professionals gather to study together remotely. CSW's Study Stream provides 24/7 virtual study rooms where you can join others from around the world, stay accountable, and boost your productivity through shared focus and community support.",
  },
  {
    question: "Is Study Stream really free?",
    answer: "Yes, CSW's Study Stream is completely free with no hidden fees or time limits. You can join unlimited study sessions, access all features, and connect with the global community without ever paying. Our philosophy is simple: you shouldn't have to pay to be productive.",
  },
  {
    question: "How does studying with strangers online help productivity?",
    answer: "Studying with strangers online creates accountability and motivation through the 'social facilitation effect.' When you see others focused on their work, it triggers psychological responses that enhance your own concentration. The presence of peers, even virtually, helps combat loneliness and procrastination.",
  },
  {
    question: "What is a focus room?",
    answer: "A focus room is a dedicated virtual space designed for deep concentration and distraction-free work. CSW's focus rooms feature minimalist interfaces, customizable Pomodoro timers, ambient music options, and a community of focused learners to help you stay in the zone.",
  },
  {
    question: "Can I use Study Stream 24/7?",
    answer: "Absolutely! CSW's Study Stream operates 24/7 with rooms always open. No matter your time zone or schedule, you'll find other learners online ready to study together. Whether it's 3 AM or 3 PM, there's always a seat waiting for you.",
  },
  {
    question: "Do I need to turn on my camera?",
    answer: "No, camera use is completely optional. Many users prefer to study with their camera off or pointed at their desk/books for privacy. You can participate fully in Study Stream sessions without ever showing your face.",
  },
  {
    question: "What features does Study Stream offer?",
    answer: "Study Stream offers synchronized Pomodoro timers, live group chat, goal sharing, progress tracking, gamification with achievements, integrated music player, synchronized YouTube watching, mindfulness breaks, task management tools, and a supportive global community.",
  },
  {
    question: "How is Study Stream different from other study platforms?",
    answer: "Unlike competitors that charge fees or limit free usage, Study Stream is completely free with unlimited access. We combine virtual study rooms with gamification, wellness features, and community support – all without any paywalls or restrictions.",
  },
];

export default function StudyStreamPage() {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study Stream - Free 24/7 Study Room & Focus Room Online"
        description="Join CSW's free 24/7 Study Stream - the ultimate focus room to study together with strangers online. Unlimited virtual study rooms, gamified experience, and global community. No payment required."
        keywords="study stream, 24/7 study room, focus room, study together, study with strangers online, virtual study room, study with me live, online study session, free study room, 24/7 studystream"
        canonicalUrl="/study-stream/"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" aria-hidden="true" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" aria-hidden="true" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} aria-hidden="true" />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Infinity className="w-4 h-4 text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-primary">NO TIME LIMIT • FREE FOR EVERYONE</span>
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <span className="text-gradient">Study Stream</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              The Ultimate Study Solution for Staying Productive and Connected 24/7
            </p>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: '0.25s' }}>
              Study, focus, and achieve more together – anytime, anywhere. This innovative platform blends traditional and online study techniques, 
              creating a space where students can work independently or engage with peers for continuous support, accountability, and motivation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try Now - Be Productive in 10 Seconds <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
            
            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" aria-hidden="true" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" aria-hidden="true" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <article className="space-y-6 text-foreground">
              <p className="text-muted-foreground text-lg leading-relaxed">
                Are you looking for a more efficient way to stay on track with your studies? Do you need constant support to stay motivated? 
                CSW's Study Stream may be exactly what you need. This innovative platform blends traditional and online study techniques, 
                creating a space where students can work independently or engage with peers for continuous support, accountability, and motivation. 
                Learn more about <Link to="/blog/science-behind-study-with-me/" className="text-primary hover:underline">the science behind study with me</Link> and 
                how it can transform your productivity.
              </p>
              
              <p className="text-muted-foreground text-lg leading-relaxed">
                Study Stream offers a flexible, interactive study experience that goes beyond the usual solo or classroom study approaches. 
                Here, you'll find both asynchronous and live study options that allow you to work at your own pace, join focus sessions, 
                and connect with others. Whether you prefer to <Link to="/study-with-me/" className="text-primary hover:underline">study with me</Link> sessions 
                or <Link to="/group-study/" className="text-primary hover:underline">group study</Link> environments, we have you covered. <strong className="text-foreground">Don't pay to be productive.</strong> CSW's Study Stream is completely 
                free and has no limits on how long or how often you can join.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Community & Social Features */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="community-title">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="community-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Study Stream Community: <span className="text-gradient">Study Together Online</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                One of the biggest benefits of Study Stream is the sense of community. When you join a session, you're entering a space 
                filled with other students and professionals who are focused on their tasks.
              </p>
            </header>
            
            <article className="max-w-4xl mx-auto mb-12 space-y-6">
              <p className="text-muted-foreground text-lg leading-relaxed">
                This shared focus creates a quiet form of camaraderie and accountability. Being in the company of others, even virtually, 
                activates psychological triggers that enhance your ability to concentrate and commit to your work. Seeing everyone around 
                you studying diligently encourages you to stay on track. Discover how <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="text-primary hover:underline">turning on your camera can boost productivity</Link> and 
                explore the <Link to="/blog/benefits-of-online-learning-community/" className="text-primary hover:underline">benefits of online learning communities</Link>.
              </p>
              
              <Card className="p-6 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                <p className="text-foreground text-lg italic">
                  "Psychologists observing the 'study with me' trend have noted it's a creative way to overcome loneliness for students, 
                  satisfying the need for belonging and social contact. Simply put, you're not alone – whenever you feel isolated or 
                  unmotivated, the Study Stream community is there to lift you up."
                </p>
              </Card>
            </article>
            
            <div className="grid md:grid-cols-2 gap-8">
              {socialFeatures.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gamified Study Experience */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="gamification-title">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" aria-hidden="true" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="gamification-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Gamified Study Stream: <span className="text-gradient">Interactive Study Experience</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Staying motivated while studying alone can be tough, which is why CSW's gamified study sessions turn studying into a rewarding experience.
              </p>
            </header>
            
            <article className="max-w-4xl mx-auto mb-12 space-y-6">
              <p className="text-muted-foreground text-lg leading-relaxed">
                You can track your progress, celebrate achievements, and even set personal study goals. Leaderboards show your standing 
                within the community, offering a fun incentive to keep pushing towards your academic aspirations. Earn rewards as you 
                reach milestones, creating an engaging and goal-driven study routine. Check out our <Link to="/blog/how-to-stop-procrastination/" className="text-primary hover:underline">guide on how to stop procrastination</Link> and 
                explore <Link to="/blog/apps-boost-productivity-college/" className="text-primary hover:underline">apps that boost productivity in college</Link>.
              </p>
              
              <p className="text-muted-foreground text-lg leading-relaxed">
                Learn more about <a href="https://msrachelhollis.com/2023/10/19/how-to-be-more-productive-at-work/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">how to be more productive at work</a> and 
                discover <a href="https://www.innopharmaeducation.com/blog/how-to-be-productive-when-studying-10-hacks-and-tips" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">10 productivity hacks for studying</a> to 
                maximize your study sessions.
              </p>
            </article>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {gamificationFeatures.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in text-center"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 mx-auto">
                    <feature.icon className="w-6 h-6 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* YouTube & Music Integration */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="media-title">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="media-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Study Stream Focus Room: <span className="text-gradient">Music & Media Integration</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Need a break or extra help focusing? CSW's synchronized features let you and your study buddies enjoy media together.
              </p>
            </header>
            
            <article className="max-w-4xl mx-auto mb-12">
              <p className="text-muted-foreground text-lg leading-relaxed">
                The synchronized YouTube-watching feature lets you and your study buddies watch educational videos or relax to chill 
                playlists together – ideal for group study or winding down. The integrated music player also provides a range of 
                ambiance options to match your study mood, keeping you in the zone with just the right vibe.
              </p>
            </article>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mediaFeatures.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in text-center"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 mx-auto">
                    <feature.icon className="w-6 h-6 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Group Study Sessions */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="group-study-title">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" aria-hidden="true" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="group-study-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Study Stream Sessions: <span className="text-gradient">Study Together, Stay Motivated</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Beyond the always-on study rooms, CSW organizes scheduled events and themed group study sessions to keep you engaged.
              </p>
            </header>
            
            <div className="max-w-4xl mx-auto">
              <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0 glow-primary">
                    <Timer className="w-8 h-8 text-primary-foreground" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-3 text-foreground">Pomodoro Sprint Sessions</h3>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      Join timed study sprints where everyone in the room follows a synchronized Pomodoro timer (e.g. 25 minutes study, 
                      5 minutes break). Working in sync with others can boost your focus and give a sense of "we're in this together." 
                      Short breaks in between let participants briefly chat about what they accomplished in the last sprint, then refocus 
                      for the next round. Try our dedicated <Link to="/study-timer/" className="text-primary hover:underline">Study Timer</Link> for 
                      customizable Pomodoro sessions.
                    </p>
                  </div>
                </div>
              </Card>
              
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
                  <h3 className="text-xl font-bold mb-3 text-foreground">Study Buddy Support</h3>
                  <p className="text-muted-foreground">
                    Pair up with a buddy for real-time accountability or shared study sessions, creating an interactive support system to keep you on track. 
                    Explore our <Link to="/study-with-friends/" className="text-primary hover:underline">Study with Friends</Link> feature for more options.
                  </p>
                </Card>
                <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
                  <h3 className="text-xl font-bold mb-3 text-foreground">Focus Study Sessions</h3>
                  <p className="text-muted-foreground">
                    For those who benefit from structured study blocks, Focus Study Sessions utilize customizable timers to help you dive deep into tasks. 
                    Or try <Link to="/study-with-strangers/" className="text-primary hover:underline">studying with strangers</Link> for fresh motivation.
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Personalized Study Support */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="support-title">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="support-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Study Stream Support: <span className="text-gradient">Flexible Study Solutions</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Study Stream is designed to meet the diverse needs of students at every level, from high school to college graduates.
              </p>
            </header>
            
            <article className="max-w-4xl mx-auto mb-12 space-y-6">
              <p className="text-muted-foreground text-lg leading-relaxed">
                The platform offers online study rooms, task management tools, and study flow options that can be customized for each user. 
                CSW also hosts interactive boot camps to dive into specific subjects, allowing you to deepen your understanding with expert guidance. 
                Enhance your learning with our <Link to="/flash-cards/" className="text-primary hover:underline">Flash Cards</Link> and <Link to="/quizzes/" className="text-primary hover:underline">Quizzes</Link> features, 
                and never miss a deadline with <Link to="/reminders/" className="text-primary hover:underline">Reminders</Link>. Discover more <Link to="/blog/top-10-digital-tools-for-academic-success/" className="text-primary hover:underline">digital tools for academic success</Link>.
              </p>
              
              <p className="text-muted-foreground text-lg leading-relaxed">
                For evidence-based study strategies, check out <a href="https://learningcenter.unc.edu/tips-and-tools/studying-101-study-smarter-not-harder/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">UNC's guide to studying smarter, not harder</a> – 
                these techniques pair perfectly with Study Stream's tools for maximum effectiveness.
              </p>
            </article>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {supportFeatures.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in text-center"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 mx-auto">
                    <feature.icon className="w-6 h-6 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Wellness & Mindfulness */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="wellness-title">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" aria-hidden="true" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="wellness-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Study Stream Wellness: <span className="text-gradient">Mindfulness Integration</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                CSW emphasizes the importance of balancing productivity with well-being.
              </p>
            </header>
            
            <article className="max-w-4xl mx-auto mb-12 space-y-6">
              <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                <p className="text-foreground text-lg leading-relaxed">
                  With guided mindfulness sessions, students can incorporate mindfulness techniques to manage stress and avoid burnout, 
                  allowing for improved focus and energy. These short mental refreshers help you recharge, improving both academic 
                  performance and overall well-being. Explore <Link to="/blog/apps-boost-self-growth-college/" className="text-primary hover:underline">apps that boost self-growth in college</Link> for 
                  more wellness resources.
                </p>
              </Card>
              
              <p className="text-muted-foreground text-lg leading-relaxed">
                Research shows that <a href="https://www.mentalhealthjournal.org/articles/mental-health-education-awareness-and-stigma-regarding-mental-illness-among-college-students.html" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">mental health awareness among college students</a> is 
                crucial for academic success. The <a href="https://www.who.int/news-room/fact-sheets/detail/adolescent-mental-health" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">WHO reports on adolescent mental health</a> highlight 
                the importance of supportive environments for young learners, which is exactly what Study Stream provides.
              </p>
            </article>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {wellnessFeatures.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in text-center"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 mx-auto">
                    <feature.icon className="w-6 h-6 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Membership and Access */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="membership-title">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="membership-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                24/7 Study Stream Access: <span className="text-gradient">Free and Unlimited</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                No paywalls, no limits – just productivity. CSW's Study Stream is free to use, with unlimited session time and no cap 
                on how often you can join.
              </p>
            </header>
            
            <article className="max-w-4xl mx-auto mb-12">
              <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                <p className="text-xl font-semibold text-foreground mb-4">Our Philosophy: You shouldn't have to pay to be productive.</p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Unlike some other platforms that restrict usage for free members or require costly subscriptions for full access, 
                  Study Stream's core features are completely free for everyone. Whether you're a high school student, a university 
                  scholar, or a working professional, you can join unlimited streams, anytime, at no cost.
                </p>
              </Card>
            </article>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {accessFeatures.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in text-center"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 mx-auto">
                    <feature.icon className="w-6 h-6 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Virtual Study Rooms */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="rooms-title">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" aria-hidden="true" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="rooms-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                24/7 Study Room: <span className="text-gradient">Virtual Focus Room Online</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Imagine having a personal desk in a library that is accessible at any hour, wherever you are – that's what CSW's 
                virtual study rooms offer.
              </p>
            </header>
            
            <article className="max-w-4xl mx-auto mb-12 space-y-6">
              <p className="text-muted-foreground text-lg leading-relaxed">
                Each Study Stream session is essentially a virtual room where you and others gather to work quietly, just like sitting 
                together in a real library or coffee shop, but online. Explore our full <Link to="/online-workspace/" className="text-primary hover:underline">Online Workspace</Link> features 
                and learn about our <Link to="/productivity-tool/" className="text-primary hover:underline">Productivity Tool</Link> suite. Here's what makes our virtual study rooms so effective for productivity:
              </p>
            </article>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {roomFeatures.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
            
            <article className="max-w-4xl mx-auto">
              <Card className="p-8 bg-card/50 backdrop-blur-sm border-border/50">
                <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                  Additionally, all your notes and tasks live in one place in this online workspace. Since CSW is cloud-based, every note 
                  you jot down or link you save in your study session is accessible later from any device.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Transitioning between school and work is easy too – maybe you have one workspace tab for your class project and another 
                  for your work tasks. Both can be managed through CSW, allowing you to maintain a clear structure across different 
                  responsibilities without juggling multiple apps.
                </p>
              </Card>
            </article>
          </div>
        </div>
      </section>

      {/* Global Student Community */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="global-title">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="global-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Study With Strangers Online: <span className="text-gradient">Global Community</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                CSW connects you to a global community, offering a space for collaboration, motivation, and friendship.
              </p>
            </header>
            
            <article className="max-w-4xl mx-auto mb-12">
              <p className="text-muted-foreground text-lg leading-relaxed">
                Through study rooms and community events, you can chat, work together, and even get career advice from other students worldwide. 
                Shared accountability and positive peer pressure create a transformative space that enhances productivity and motivation through peer support. 
                Try our <Link to="/zoom-study-room/" className="text-primary hover:underline">Zoom Study Room</Link> for video-based collaboration and 
                learn about <Link to="/blog/apps-boost-organization-college/" className="text-primary hover:underline">apps that boost organization in college</Link>.
              </p>
            </article>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {globalCommunityFeatures.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in text-center"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 mx-auto">
                    <feature.icon className="w-6 h-6 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" aria-hidden="true" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <header className="text-center mb-16">
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                How Study Stream <span className="text-gradient">Works</span>
              </h2>
              <p className="text-xl text-muted-foreground">Get started in three simple steps</p>
            </header>
            
            <div className="grid md:grid-cols-3 gap-8">
              {howItWorks.map((item, index) => (
                <div key={item.step} className="text-center space-y-6 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                  <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center glow-primary">
                    <span className="text-3xl font-bold text-primary-foreground">{item.step}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing/Free Section */}
      <StudyStreamSection />

      {/* FAQ Section */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="faq-title">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" aria-hidden="true" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <header className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <HelpCircle className="w-4 h-4 text-primary" aria-hidden="true" />
                <span className="text-sm font-semibold text-primary">FAQ</span>
              </div>
              <h2 id="faq-title" className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Study Stream <span className="text-gradient">FAQ</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Common questions about virtual study rooms, focus rooms, and studying together online.
              </p>
            </header>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl px-6 data-[state=open]:border-primary/50"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-foreground hover:text-primary py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* FAQ Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqData.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://csw.live"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Study Stream",
                "item": "https://csw.live/study-stream/"
              }
            ]
          })
        }}
      />

      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "CSW",
            "alternateName": "CSW.live",
            "url": "https://csw.live",
            "logo": "https://csw.live/favicon.ico",
            "sameAs": [
              "https://app.csw.live"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer support",
              "availableLanguage": ["English", "Korean", "Japanese", "Chinese", "Vietnamese"]
            }
          })
        }}
      />

      {/* VideoObject Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": "Study Stream Demo - How It Works",
            "description": "See how CSW's Study Stream provides free 24/7 virtual study rooms, focus sessions, and a global community for productive studying.",
            "thumbnailUrl": "https://csw.live/videos/productivity-demo.mp4",
            "uploadDate": "2024-01-01",
            "contentUrl": "https://csw.live/videos/productivity-demo.mp4",
            "embedUrl": "https://csw.live/study-stream/",
            "duration": "PT1M",
            "publisher": {
              "@type": "Organization",
              "name": "CSW",
              "logo": {
                "@type": "ImageObject",
                "url": "https://csw.live/favicon.ico"
              }
            }
          })
        }}
      />

      {/* WebPage Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Study Stream - Free 24/7 Study Room & Focus Room Online",
            "description": "Join CSW's free 24/7 Study Stream - the ultimate focus room to study together with strangers online. Unlimited virtual study rooms, gamified experience, and global community.",
            "url": "https://csw.live/study-stream/",
            "inLanguage": "en",
            "datePublished": "2024-01-01",
            "dateModified": "2025-01-01",
            "isPartOf": {
              "@type": "WebSite",
              "name": "CSW - Online Workspace",
              "url": "https://csw.live"
            },
            "about": {
              "@type": "Thing",
              "name": "Virtual Study Rooms"
            },
            "mentions": [
              { "@type": "Thing", "name": "24/7 Study Room" },
              { "@type": "Thing", "name": "Focus Room" },
              { "@type": "Thing", "name": "Study Together" },
              { "@type": "Thing", "name": "Study With Strangers Online" },
              { "@type": "Thing", "name": "Pomodoro Timer" },
              { "@type": "Thing", "name": "Virtual Coworking" },
              { "@type": "Thing", "name": "Body Doubling" }
            ],
            "keywords": "study stream, 24/7 study room, focus room, study together, study with strangers online, virtual study room, pomodoro timer, body doubling, virtual coworking",
            "mainEntity": {
              "@type": "SoftwareApplication",
              "name": "CSW Study Stream"
            }
          })
        }}
      />

      {/* SoftwareApplication Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CSW Study Stream",
            "applicationCategory": "EducationalApplication",
            "applicationSubCategory": "Productivity",
            "operatingSystem": "Web Browser",
            "description": "Free 24/7 virtual study rooms and focus rooms. Study together with strangers online, use Pomodoro timers, track progress, and join a global community of learners.",
            "url": "https://app.csw.live",
            "downloadUrl": "https://app.csw.live",
            "softwareVersion": "2.0",
            "releaseNotes": "Enhanced study rooms with gamification, wellness features, and global community support.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock",
              "priceValidUntil": "2026-12-31"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5",
              "ratingCount": "3000",
              "bestRating": "5",
              "worstRating": "1"
            },
            "featureList": [
              "24/7 Virtual Study Rooms",
              "Focus Room with Pomodoro Timer",
              "Study Together with Global Community",
              "Study With Strangers Online",
              "Gamified Study Experience with Achievements",
              "Synchronized YouTube Watching",
              "Integrated Lo-Fi Music Player",
              "Goal Sharing and Progress Tracking",
              "Mindfulness and Wellness Integration",
              "Task Management and To-Do Lists",
              "Free Unlimited Access Forever",
              "No Ads or Paywalls"
            ],
            "screenshot": "https://csw.live/videos/productivity-demo.mp4",
            "author": {
              "@type": "Organization",
              "name": "CSW",
              "url": "https://csw.live"
            },
            "publisher": {
              "@type": "Organization",
              "name": "CSW",
              "url": "https://csw.live"
            }
          })
        }}
      />

      {/* HowTo Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "How to Use Study Stream for Productive Studying",
            "description": "Learn how to join CSW's free 24/7 Study Stream virtual study rooms and start studying with a global community in just 3 simple steps.",
            "totalTime": "PT2M",
            "estimatedCost": {
              "@type": "MonetaryAmount",
              "currency": "USD",
              "value": "0"
            },
            "supply": [],
            "tool": [
              {
                "@type": "HowToTool",
                "name": "Web browser (Chrome, Firefox, Safari, Edge)"
              },
              {
                "@type": "HowToTool",
                "name": "Internet connection"
              },
              {
                "@type": "HowToTool",
                "name": "Webcam (optional)"
              }
            ],
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Choose a Room",
                "text": "Browse available study rooms or create your own. Pick a vibe that matches your mood - from silent focus rooms to lo-fi music rooms.",
                "url": "https://app.csw.live"
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Join and Connect",
                "text": "Enter the room with one click. Enable your webcam to join the community (optional). Set your study goals and timer preferences.",
                "url": "https://app.csw.live"
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Study Together",
                "text": "Focus on your work while surrounded by others doing the same. Stay motivated and accountable with shared goals and progress tracking.",
                "url": "https://app.csw.live"
              }
            ]
          })
        }}
      />

      {/* Final CTA */}
      <section className="py-24 relative overflow-hidden" aria-labelledby="cta-title">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" aria-hidden="true" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" aria-hidden="true" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 id="cta-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Join 24/7 StudyStream <span className="text-gradient">Free Today!</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-4 max-w-2xl mx-auto">
              CSW's Study Stream goes beyond just studying – it's a community-driven, wellness-focused, and highly customizable platform 
              designed to help you stay productive, connected, and motivated.
            </p>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              With features like customizable timers, progress tracking, gamification, and study buddies, you'll have everything you need 
              to make the most of your study sessions. Join now, set your goals, and discover how Study Stream can transform your learning journey!
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Start Studying for Free
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
